<template>
  <div class="mt-4">
    <v-card class="mx-auto" max-width="95%" elevation="2">
      <v-toolbar dark>
        <v-toolbar-title>프롬북 · 피드 관리</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-chip small outlined dark class="mr-2">
          <v-icon left x-small>mdi-newspaper-variant</v-icon>
          총 {{ total.toLocaleString() }}건
        </v-chip>
      </v-toolbar>

      <!-- 필터 영역 -->
      <v-card-text class="pb-0">
        <v-row dense align="center">
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="filter.keyword"
              label="검색어 (제목/본문)"
              outlined dense hide-details clearable
              prepend-inner-icon="mdi-magnify"
              @keyup.enter="onSearch"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="2">
            <v-select
              v-model="filter.is_hidden"
              :items="hiddenOptions"
              label="공개 상태"
              outlined dense hide-details
            ></v-select>
          </v-col>
          <v-col cols="12" sm="2">
            <v-select
              v-model="filter.page_size"
              :items="[50, 100, 200]"
              label="페이지 사이즈"
              outlined dense hide-details
            ></v-select>
          </v-col>
          <v-col cols="12" sm="1">
            <v-btn color="primary" depressed block @click="onSearch">
              <v-icon>mdi-magnify</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- 피드 리스트 -->
      <v-card-text class="pa-0 pt-2">
        <div style="overflow-x: auto;">
          <v-data-table
            :headers="headers"
            :items="list"
            :items-per-page="filter.page_size"
            :footer-props="{'items-per-page-options':[50, 100, 200]}"
            :loading="loading"
            :server-items-length="total"
            class="elevation-0"
          >
            <template v-slot:item.thumb="{ item }">
              <v-avatar size="36" tile v-if="item.thumb">
                <v-img :src="item.thumb"></v-img>
              </v-avatar>
              <v-icon v-else color="grey lighten-1">mdi-image-off</v-icon>
            </template>
            <template v-slot:item.body_preview="{ item }">
              <span class="text-caption">{{ (item.body || '').substring(0, 40) }}{{ item.body && item.body.length > 40 ? '...' : '' }}</span>
            </template>
            <template v-slot:item.like_count="{ item }">
              <v-chip x-small color="pink lighten-4">{{ item.like_count || 0 }}</v-chip>
            </template>
            <template v-slot:item.comment_count="{ item }">
              <v-chip x-small color="blue lighten-4">{{ item.comment_count || 0 }}</v-chip>
            </template>
            <template v-slot:item.report_count="{ item }">
              <v-chip x-small :color="item.report_count > 0 ? 'red lighten-4' : 'grey lighten-3'">
                {{ item.report_count || 0 }}
              </v-chip>
            </template>
            <template v-slot:item.is_hidden="{ item }">
              <v-chip x-small :color="item.is_hidden ? 'grey' : 'green'" dark>
                {{ item.is_hidden ? '숨김' : '노출' }}
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn icon small @click="openDetail(item)" title="상세보기">
                <v-icon small>mdi-magnify</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </div>
        <v-container v-if="!loading && list.length === 0" class="text-center py-8">
          <v-icon size="56" color="grey lighten-1">mdi-newspaper-variant-outline</v-icon>
          <p class="text-body-1 grey--text mt-3">검색 결과가 없습니다.</p>
        </v-container>
      </v-card-text>
    </v-card>

    <!-- 상세 다이얼로그 -->
    <v-dialog v-model="dialog" max-width="750" scrollable>
      <v-card>
        <v-card-title>
          <v-icon left>mdi-newspaper-variant</v-icon>
          피드 상세
          <v-spacer></v-spacer>
          <v-chip small outlined>{{ selected.id }}</v-chip>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <v-simple-table dense>
            <tbody>
              <tr><td class="font-weight-bold" style="width:140px">제목</td><td>{{ selected.title }}</td></tr>
              <tr><td class="font-weight-bold">본문</td><td style="white-space:pre-wrap;max-width:450px">{{ selected.body }}</td></tr>
              <tr><td class="font-weight-bold">썸네일</td>
                <td>
                  <v-img v-if="selected.thumb" :src="selected.thumb" max-width="120" max-height="80"></v-img>
                  <span v-else>-</span>
                </td>
              </tr>
              <tr><td class="font-weight-bold">좋아요</td><td>{{ selected.like_count }}</td></tr>
              <tr><td class="font-weight-bold">댓글수</td><td>{{ selected.comment_count }}</td></tr>
              <tr><td class="font-weight-bold">신고수</td><td>{{ selected.report_count }}</td></tr>
              <tr><td class="font-weight-bold">작성자 ID</td><td>{{ selected.author_user_id }}</td></tr>
              <tr><td class="font-weight-bold">작성일</td><td>{{ selected.created_at }}</td></tr>
              <tr><td class="font-weight-bold">공개 상태</td>
                <td>
                  <v-chip x-small :color="selected.is_hidden ? 'grey' : 'green'" dark>
                    {{ selected.is_hidden ? '숨김' : '노출' }}
                  </v-chip>
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <!-- 댓글 미리보기 -->
          <div class="mt-4" v-if="selectedComments.length > 0">
            <div class="subtitle-2 mb-2">댓글 미리보기 (최대 50개)</div>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>댓글 ID</th>
                  <th>작성자</th>
                  <th>내용</th>
                  <th>작성일</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="c in selectedComments" :key="c.id">
                  <td>{{ c.id }}</td>
                  <td>{{ c.author_user_id }}</td>
                  <td>{{ (c.body || '').substring(0, 50) }}</td>
                  <td>{{ c.created_at }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn small :color="selected.is_hidden ? 'green' : 'orange'" dark @click="toggleHide">
            <v-icon left small>mdi-eye{{ selected.is_hidden ? '' : '-off' }}</v-icon>
            {{ selected.is_hidden ? '노출' : '숨김' }}
          </v-btn>
          <v-btn small color="red" dark @click="deleteFeed">
            <v-icon left small>mdi-delete</v-icon>삭제
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false">닫기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="py-12"></div>
  </div>
</template>

<script>
import { prombook } from '../../api'

export default {
  name: 'Prombook_FeedList',
  data() {
    return {
      loading: false,
      total: 0,
      list: [],
      filter: {
        keyword: '',
        is_hidden: null,
        page: 1,
        page_size: 50,
      },
      dialog: false,
      selected: {},
      selectedComments: [],
      hiddenOptions: [
        { text: '전체', value: null },
        { text: '숨김', value: true },
        { text: '노출', value: false },
      ],
      headers: [
        { text: 'ID', value: 'id', width: '80px' },
        { text: '썸네일', value: 'thumb', width: '70px', sortable: false },
        { text: '제목', value: 'title', width: '180px' },
        { text: '본문 미리보기', value: 'body_preview', width: '200px', sortable: false },
        { text: '좋아요', value: 'like_count', width: '80px' },
        { text: '댓글', value: 'comment_count', width: '70px' },
        { text: '신고', value: 'report_count', width: '70px' },
        { text: '작성자', value: 'author_user_id', width: '120px' },
        { text: '작성일', value: 'created_at', width: '150px' },
        { text: '상태', value: 'is_hidden', width: '80px' },
        { text: '상세', value: 'actions', width: '60px', sortable: false },
      ],
    }
  },
  methods: {
    async load() {
      try {
        this.loading = true
        const r = await prombook.search_feed(
          this.filter.keyword,
          this.filter.is_hidden,
          this.filter.page,
          this.filter.page_size
        )
        this.list = r.feed_list || []
        this.total = r.total || 0
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    onSearch() {
      this.filter.page = 1
      this.load()
    },
    async openDetail(item) {
      this.selected = JSON.parse(JSON.stringify(item))
      this.selectedComments = []
      this.dialog = true
      try {
        const r = await prombook.get_feed_detail(item.id)
        if (r.feed_info) this.selected = r.feed_info
        this.selectedComments = (r.comment_list || []).slice(0, 50)
      } catch (e) {
        console.error(e)
      }
    },
    async toggleHide() {
      if (!confirm(`이 피드를 ${this.selected.is_hidden ? '노출' : '숨김'} 처리하시겠습니까?`)) return
      try {
        await prombook.hide_feed(this.selected.id, !this.selected.is_hidden)
        alert('처리되었습니다.')
        this.dialog = false
        this.load()
      } catch (e) {
        console.error(e)
        alert('처리에 실패했습니다.')
      }
    },
    async deleteFeed() {
      if (!confirm('이 피드를 삭제하시겠습니까?')) return
      try {
        await prombook.delete_feed(this.selected.id)
        alert('삭제되었습니다.')
        this.dialog = false
        this.load()
      } catch (e) {
        console.error(e)
        alert('삭제에 실패했습니다.')
      }
    },
  },
  mounted() {
    this.load()
  },
}
</script>
